import { useId } from 'react';
import type { InputHTMLAttributes } from 'react';

import styles from './Field.module.css';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  /** Komunikat błędu — już przetłumaczony. Obecność oznacza stan nieprawidłowy. */
  error?: string | undefined;
  hint?: string | undefined;
  /**
   * Etykieta **istnieje, ale jest niewidoczna** — dla pól, przy których widoczny podpis
   * byłby szumem, a kształt i tak mówi, co to jest (szukajka w pasku narzędzi).
   *
   * ⛔ To nie jest to samo co brak etykiety: bez niej czytnik ekranu ogłasza „pole edycji"
   * bez nazwy. Placeholder etykiety nie zastępuje — znika przy pisaniu.
   */
  labelHidden?: boolean;
}

/**
 * Pole formularza razem z etykietą i błędem.
 *
 * Etykieta, pole i komunikat są **związane identyfikatorami**, a nie tylko
 * ustawione obok siebie. To nie jest formalność: bez `htmlFor` czytnik ekranu
 * czyta „pole edycji" bez nazwy, a bez `aria-describedby` nie przeczyta powodu
 * odrzucenia formularza — czyli osoba niewidoma dowie się, że coś jest źle,
 * ale nie co.
 */
export function Field({ label, error, hint, labelHidden, className, ...rest }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : undefined, hint ? hintId : undefined]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles['field']}>
      <label
        className={labelHidden ? 'visually-hidden' : styles['label']}
        htmlFor={id}
      >
        {label}
      </label>

      <input
        id={id}
        className={[styles['input'], error ? styles['invalid'] : undefined, className]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        {...rest}
      />

      {hint ? (
        <span id={hintId} className={styles['hint']}>
          {hint}
        </span>
      ) : null}

      {/* `role="alert"` — komunikat pojawiający się po wysłaniu formularza musi
          zostać ogłoszony, bo wzrok użytkownika jest wtedy przy przycisku. */}
      {error ? (
        <span id={errorId} className={styles['error']} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
